import { ref } from 'vue';
import axios from '@/plugins/axios';
import i18next from '@/config/i18n';
import { useSnackbar } from '@/composables/useSnackbar';
import { invalidateFolders } from '@/composables/useExperimentQuery';
import { buildContextPatch } from '@/constants/creativeContext';
import { thumbnailVersion } from '@/composables/useThumbnailCache';
import {
    readWizardState,
    readStoredBaseline,
    readStoredContext,
    markWizardFinished,
    markWizardPending,
    WIZARD_STEP_BRIEF,
    WIZARD_STEP_LAYERS,
} from '@/constants/wizardGate';

// --- Retomar o wizard de uma peça que já está no ar ---
//
// Palavra do dono do produto (2026-09-09): "se o usuário não termina de
// preencher o modal e ele clica na peça, ele volta de onde parou no
// preenchimento". É a metade de volta do portão: como insight e NeuroGen ficam
// esperando o fim do wizard, quem fecha no meio precisa de um caminho de volta
// — senão a peça fica travada e o cliente não sabe por quê.
//
// O QUE VOLTA É TUDO O QUE FOI PREENCHIDO. Palavra do dono do produto
// (2026-09-09), que REVERTE a regra anterior ("volta a etapa, não as teclas"):
// "tudo que eu selecionar aqui é pra ser gravado pra caso eu saia da tela e
// volte ainda continuar aparecendo". A etapa 2 grava campo a campo enquanto o
// cliente mexe (`onDraft`, abaixo), então o que a retomada lê do servidor já é
// o formulário como ele o deixou — e não só o que ele mandou ao avançar.
//
// Este arquivo NÃO conhece o formato do payload — quem lê o contrato é
// `constants/wizardGate.js`.

// O wizard do upload tem um orquestrador inteiro (`useMediaUpload`) porque há
// fila de envio, retry e barreira de gravação. Na retomada não há upload: a
// peça já existe. Sobra o que este arquivo faz — gravar o brief e carimbar o
// fim, nessa ordem.
export const canResumeWizard = (experiment) => readWizardState(experiment) !== null;

// --- UM CLIQUE, UMA RETOMADA (spec briefing-prioridades-ux A11, 2026-10-05) ---
// A busca do formulário (`GET /experiments/{id}`, abaixo) chegou a levar vários
// segundos com outra peça processando no mesmo projeto. Sem sinal na tela, o
// cliente clicava de novo no card, e cada clique virava outra busca e outro
// `showBriefing` — que, ao voltar, REESCREVIA o formulário com um retrato
// tirado antes das respostas que ele já tinha dado (a "resposta perdida" da
// vivência de 05/10). Duas travas fecham isso:
//   - com o diálogo aberto, uma retomada não reabre nem reescreve nada;
//   - com uma busca em voo para o mesmo diálogo, o segundo pedido é o mesmo
//     pedido — não sai outra busca.
// E a janela abre NO CLIQUE (`showResumePending`), com esqueleto, em vez de
// esperar a busca: o primeiro clique sempre tem resposta visível.
// Por diálogo (WeakMap) e não por módulo: cada `Main.vue` tem o seu, e o
// estado não atravessa instâncias.
//
// D11 (2026-10-05, V13: "o primeiro clique depois de fechar não abre, 4/4"): a
// trava de voo era uma marca SEM DONO. Fechar a janela com a busca ainda em voo
// (comum com a listagem recarregando: a busca levou até ~7 s) deixava a marca de
// pé até a busca voltar, e o clique seguinte caía nela — `true` para quem chamou,
// nenhuma janela na tela. Agora a marca é um bilhete por retomada: ela só segura
// o clique enquanto a janela ainda é DESTA retomada (`isResumePending`), e a
// resposta de uma retomada abandonada não mexe na marca da seguinte.
const _resumeInFlight = new WeakMap();   // dialog -> { experimentId }

// --- A ETAPA DA RETOMADA: A MAIS ADIANTADA QUE ALGUÉM CONHECE (D11, V8 #14) ---
// O que se sabia (documentado no spec desde 22/09 e não consertado): a etapa
// saía SÓ do item da árvore, e a árvore envelhece. O `/folders` não tem relógio
// próprio — só é refeito por `status_update` terminal de alguma peça —, então
// quem avança da etapa 2 para a 3 e fecha encontra o card ainda dizendo `2`, e a
// reabertura caía no Contexto. Na vivência de base a listagem foi refeita no
// meio do caminho (havia peça processando); na V8, não.
// Duas fontes mais frescas que o card já existiam e eram jogadas fora:
//   - a memória desta sessão (`markWizardPending(id, 3)` no avanço), que o
//     `readWizardState` só consulta com o campo AUSENTE;
//   - o `GET /experiments/{id}` que a retomada já faz, e que traz
//     `wizard_pending_step`.
// A etapa só anda para a frente (`context_submitted_at` não se desfaz no
// servidor), então vale a maior das três. Peça concluída nesta sessão nem chega
// aqui (`readWizardState` devolve `null` pela trava de fim).
const _sessionStep = (experimentId) => (
    readWizardState({ id: experimentId, type: 'experiment' })?.step ?? 0
);
const _freshStep = (fresh) => {
    const step = fresh?.wizard_pending_step;
    return step === WIZARD_STEP_BRIEF || step === WIZARD_STEP_LAYERS ? step : 0;
};

export const resumeWizard = (experiment, dialog) => {
    const state = readWizardState(experiment);
    if (!state || typeof dialog?.showBriefing !== 'function') return false;

    const experimentId = experiment.id;
    const mediaId = experiment.original_media?.id ?? null;
    const mediaType = experiment.media_type === 'video' ? 'video' : 'image';

    // `true` nos dois casos: o clique FOI atendido (o wizard já está na tela ou
    // está a caminho), e quem chamou não deve navegar para a página da peça.
    if (dialog.isOpen?.()) return true;
    const inFlight = _resumeInFlight.get(dialog);
    if (inFlight) {
        // Diálogo sem a API nova não sabe dizer se a janela ainda é da retomada:
        // a marca vale até a busca voltar, como antes.
        const stillPending = typeof dialog.isResumePending !== 'function'
            || dialog.isResumePending(inFlight.experimentId);
        if (stillPending) return true;
        // A janela daquela retomada já fechou: a marca é velha, e este clique é
        // uma retomada nova.
        _resumeInFlight.delete(dialog);
    }

    // A etapa mais adiantada entre o card e a memória da sessão (ver acima).
    let startStep = Math.max(state.step, _sessionStep(experimentId));

    // Reabrir é, por si só, a prova de que a peça espera o cliente — e é o
    // momento em que esta sessão passa a saber a etapa. Registrar aqui é o que
    // segura o selo se a listagem for reconstruída sem o campo (ver a trava
    // bidirecional em `constants/wizardGate.js`). Não contradiz o servidor:
    // esta memória só é lida quando o `/folders` não trouxe resposta nenhuma.
    // Com a etapa JÁ RESOLVIDA: gravar a do card aqui apagaria o `3` que a
    // sessão aprendeu no avanço.
    markWizardPending(experimentId, startStep);

    // FILA SERIAL, pelo mesmo motivo do wizard do upload: no vídeo (e em
    // qualquer caminho em que a etapa atual seja a última) o avanço grava o
    // contexto e carimba o fim no MESMO clique, e o servidor tem de encontrar o
    // contexto já gravado quando abrir o portão. Sem a fila, os dois PATCH
    // sairiam em paralelo e a ordem seria sorteada pela rede.
    let writes = Promise.resolve();
    const enqueue = (fn) => {
        const write = writes.then(fn);
        writes = write.catch(() => {});
        return write;
    };

    // --- Rascunho: cada campo grava na hora, SEM carimbar o fim da etapa ---
    // O corpo é só de colunas de contexto: `context_submitted` fica de fora, e
    // tem de ficar — é dele que o servidor deriva `wizard_pending_step`, então
    // um rascunho que o carregasse mandaria o cliente de volta na etapa 3 sem
    // ele nunca ter avançado da 2. Entra na MESMA fila serial do brief e do
    // carimbo, para que a ordem não seja sorteada pela rede.
    const saveDraft = ({ experimentId: id, fields }) => enqueue(async () => {
        if (!fields || Object.keys(fields).length === 0) return;
        try {
            await axios.patch(`/experiments/${id ?? experimentId}`, buildContextPatch(fields));
        } catch (error) {
            console.error('[useWizardResume] context draft patch failed:', experimentId, error);
            // Sobe para o diálogo desfazer a memória e tentar de novo no
            // próximo toque; a tela não trava e nada do que foi digitado se
            // perde (o avanço manda tudo de novo).
            throw error;
        }
    });

    const saveContext = (brief) => {
        if (brief?.confirmed) {
            // A etapa 2 acabou NO CLIQUE, não na volta do PATCH (D11, janela B do
            // spec): quem avança e fecha antes de o PATCH voltar tem de reabrir
            // nas camadas. Se o PATCH falhar, a marca volta para a 2 — o servidor
            // não soube do avanço, e a retomada tem de refazê-lo. O carimbo do
            // fim apaga esta marca quando ele chegar.
            markWizardPending(experimentId, WIZARD_STEP_LAYERS);
        }
        return enqueue(async () => {
            if (!brief?.confirmed) return;
            // Peça única: o diálogo escreve no bloco geral (é o formato que o
            // wizard do upload também usa para N=1).
            const ctx = brief.creativeContext || {};
            try {
                await axios.patch(`/experiments/${experimentId}`, buildContextPatch(ctx, { context_submitted: true }));
            } catch (error) {
                console.error('[useWizardResume] context patch failed:', experimentId, error);
                markWizardPending(experimentId, WIZARD_STEP_BRIEF);
            }
        });
    };

    // UMA VEZ SÓ. O diálogo já guarda o seu próprio `_wizardEnded`; esta marca
    // é a defesa de quem chama de fora (uma segunda retomada, um clique duplo
    // na listagem ou no sino).
    // A marca só acende quando o servidor aceitou: carimbo que falha deixa a
    // porta aberta para o "Tentar novamente" do aviso (SYN-2738 U9).
    let stamped = false;
    const stampFinished = () => enqueue(async () => {
        if (stamped) return;
        try {
            await axios.patch(`/experiments/${experimentId}`, { wizard_completed: true });
            stamped = true;
            // A peça deixa de esperar o cliente NO CLIQUE, não no próximo
            // rebuild da listagem: sem a trava, o selo "FALTA VOCÊ" continuava
            // aceso por até ~30 s depois de "Concluir" (medido em 09/09), e o
            // que ele pede é exatamente o que o cliente acabou de fazer. Ver
            // `markWizardFinished` em `constants/wizardGate.js`.
            //
            // O QUE O SELO VIRA: nada de "pronto". Concluir é o que LIBERA
            // insight e NeuroGen, então a peça entra em geração — e é a tarja de
            // processamento que o card mostra em seguida, porque
            // `isExperimentFullyReady` continua falso enquanto os insights e os
            // protótipos não voltarem.
            markWizardFinished(experimentId);
            // Invalidar continua sendo o contrato de toda mutação aqui: a trava
            // resolve o AGORA, o refetch traz o resto do payload novo.
            invalidateFolders();
        } catch (error) {
            console.error('[useWizardResume] wizard completion patch failed:', experimentId, error);
            useSnackbar().showSnackbar(
                i18next.t('composables.useMediaUpload.snackbar.wizardStampFailed', { name: experiment.name || '' }),
                'error',
                -1,
                undefined,
                { label: i18next.t('composables.useMediaUpload.snackbar.retry'), onClick: stampFinished },
            );
        }
    });

    // --- DE ONDE SAI O FORMULÁRIO PREENCHIDO: DO SERVIDOR, NÃO DA LISTAGEM ---
    // `ExperimentSummarySchema` traz `wizard_pending_step` e mais NADA do brief
    // (medido na vivência 09/09): as cinco colunas de contexto não existem no
    // payload da árvore. Abrir o formulário com o item do card, portanto, mostra
    // cinco campos vazios para quem já preencheu — o defeito que este trabalho
    // veio fechar. Uma ida ao servidor por CLIQUE (não por campo) é o preço de
    // devolver o formulário como ele ficou.
    //
    // Falhar não cancela a retomada: o wizard abre com o que a listagem tem
    // (campos vazios) em vez de não abrir. A peça continua esperando o cliente,
    // e é pior deixá-lo sem porta do que sem os valores.
    const ticket = { experimentId };
    const openBriefing = (fresh) => {
        // Resposta de uma retomada que já foi substituída por outra: não é
        // dela a marca, nem a janela.
        if (_resumeInFlight.get(dialog) !== ticket) return;
        _resumeInFlight.delete(dialog);
        // O cliente fechou a janela enquanto a busca viajava (X, Esc): a pausa
        // dele vence. Reabrir por conta própria seria o "abre sozinho" — o
        // espelho do defeito que esta trava veio fechar.
        if (typeof dialog.isResumePending === 'function' && !dialog.isResumePending(experimentId)) return;
        // A busca sabe a etapa melhor do que o card: se ela diz 3 e a janela
        // abriu na 2 (card e sessão sem notícia do avanço — outra aba, outro
        // aparelho), o formulário abre nas camadas.
        startStep = Math.max(startStep, _freshStep(fresh));
        markWizardPending(experimentId, startStep);
        dialog.showBriefing({
            items: [{
                uid: String(experimentId),
                filename: experiment.name || '',
                experimentId,
                mediaId,
                mediaType,
                // Não há envio nesta passagem: a peça está no ar desde o upload que
                // criou o wizard. Dizer "enviando" aqui seria inventar um relógio.
                uploadStatus: 'uploaded',
                // A detecção de áreas larga no upload e não espera o wizard, então
                // ela pode já ter terminado há muito tempo. `detecting` é só o
                // ponto de partida — o `LayersStep` pergunta ao `GET /media` e ao
                // WebSocket assim que monta, e o que ele aprende vence isto.
                aoiStatus: 'detecting',
                // A8: a miniatura que o card acabou de pintar está no cache da
                // listagem com esta versão — a etapa 2 a reaproveita sem requisição.
                thumbnailVersion: thumbnailVersion(experiment),
            }],
            folderId: experiment.folder_id ?? null,
            startStep,
            initialContext: readStoredContext(fresh ?? experiment),
            // O formulário mostra a tradução do legado; a base do diff é o que o
            // servidor tem (ver `readStoredBaseline`).
            storedContext: readStoredBaseline(fresh ?? experiment),
            resumed: true,
            onDraft: saveDraft,
            onSave: saveContext,
            onFinish: stampFinished,
        });
    };

    // A janela abre já, na etapa em que ele parou; a busca só a preenche.
    dialog.showResumePending?.({
        experimentId,
        filename: experiment.name || '',
        mediaType,
        startStep,
        folderId: experiment.folder_id ?? null,
    });
    _resumeInFlight.set(dialog, ticket);

    // `then(ok, falha)` e não `then().catch()`: só a BUSCA que falha cai no
    // caminho "abre sem os valores" — um erro dentro do preenchimento não pode
    // se passar por falha de rede e chamar o preenchimento de novo.
    axios.get(`/experiments/${experimentId}`)
        .then(
            (response) => openBriefing(response?.data ?? null),
            (error) => {
                console.error('[useWizardResume] experiment fetch failed:', experimentId, error);
                openBriefing(null);
            },
        );
    return true;
};

// --- O PEDIDO VINDO DE FORA DA LISTAGEM ---
// Quem tem o diálogo em mãos é a `Main.vue` (é lá que o `BulkUploadConfirmation`
// está montado). O sino, porém, vive no cabeçalho e pode estar aberto sobre
// QUALQUER tela — então ele não pede a retomada direto: ele deixa o pedido aqui
// e navega para a listagem. A `Main` consome o pedido ao montar e a cada
// mudança, o que resolve as duas ordens possíveis (já estava montada; acabou de
// montar por causa da navegação) sem ninguém precisar acertar o tempo.
const _pendingResumeId = ref(null);
export const pendingResumeId = _pendingResumeId;
export const requestWizardResume = (experimentId) => {
    if (experimentId) _pendingResumeId.value = String(experimentId);
};
export const takeWizardResumeRequest = () => {
    const id = _pendingResumeId.value;
    _pendingResumeId.value = null;
    return id;
};

export const useWizardResume = () => ({ canResumeWizard, resumeWizard });
